import type { SizeOptions } from '@floating-ui/dom'
import { autoUpdate, flip, offset, shift, size, useFloating } from '@floating-ui/react-dom'
import clsx from 'clsx'
import { useIsInModal } from 'contexts/ModalProvider'
import { shallowEqual } from 'hooks/reduxHooks'
import useClickOutside from 'hooks/useClickOutside'
import { getThemeClassName } from 'lib/theme/themeUtils'
import type { MouseEvent, MouseEventHandler, PropsWithChildren, RefObject } from 'react'

import {
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'

import * as React from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE } from './_DropdownSheet'
import { portaledDropdownClassName } from './const'
import useHasMounted from 'hooks/useHasMounted'

const REACT_MODAL_SELECTOR = '.ReactModalPortal, .ReactModal__Overlay, .ReactModal__Content'

function isClickInModal(target: HTMLElement | null): boolean {
  return target?.closest(REACT_MODAL_SELECTOR) != null
}

// Elements can opt out of triggering an outside-click close by adding this
// attribute. Used by in-place controls that sit outside the dropdown but should
// not dismiss it (e.g. the flights toggle in the offer search bar, which updates
// pricing while an open calendar/guest dropdown stays put).
const KEEP_OPEN_SELECTOR = '[data-dropdown-keep-open]'

function shouldKeepOpenForTarget(target: HTMLElement | null): boolean {
  return target?.closest(KEEP_OPEN_SELECTOR) != null
}

// Context to track ancestor dropdown IDs for nested dropdowns (supports multiple levels of nesting)
const FloatingDropdownContext = createContext<ReadonlyArray<string>>([])

const DropdownWrapper = styled.div`
  position: absolute;
  z-index: ${zIndex.menu};
  opacity: 0;
  pointer-events: none;
  visibility: hidden;
  transition: opacity 200ms ease-out, visibility 0s 200ms;

  &.isPortaledFromModal {
    // In this case the dropdown is outside the stacking context of the modal,
    // so we need a higher z-index so it will still appear above thee modal.
    z-index: ${zIndex.menuInModal};
  }

  &.visible {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
    transition: opacity 200ms ease-in, visibility 0s;
  }
`

type SizeArgs = Parameters<Exclude<SizeOptions['apply'], undefined>>[0]

const dynamicWidthStyle = ({ rects }: SizeArgs) => {
  return {
    width: `${rects.reference.width}px`,
  }
}

const availableWidthStyle = ({ availableWidth }: SizeArgs) => {
  return {
    maxWidth: `${Math.max(availableWidth, 0)}px`,
  }
}

export type _FloatingDropdownCloseHandler = (event: MouseEvent<HTMLElement> | KeyboardEvent, reason: 'outside-click' | 'dismiss-click' | 'internal-click' | 'escape-key') => void | (() => void)

interface Props extends PropsWithChildren, Utils.PropsWithDataAttributes {
  /**
   * Determines where the Dropdown sits relative to the anchor.
   *
   * @default bottom
   */
  placement?: 'top' | 'top-start' | 'top-end' | 'right' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left'
  /**
   * Reference to the element to which the dropdown would attach
   */
  anchorRef: RefObject<HTMLElement | null>
  /**
   * Reference to the element that's triggering the dropdown.
   *
   * While clicking outside of the dropdown closes it,
   * this element will not be considered as outside element.
   */
  triggerRef: RefObject<HTMLElement | null> | Array<RefObject<HTMLElement | null>>
  /**
   * Reference to the element within which the dropdown is bounded.
   */
  boundaryRef?: RefObject<HTMLElement | null>
  open: boolean
  onClose?: _FloatingDropdownCloseHandler
  /**
   * Space between the anchor and the floating dropdown
   *
   * @default 8
   */
  anchorOffset?: number
  disableFlip?: boolean
  crossAxisShift?: boolean
  autoHeight?: boolean
  width: 'auto' | 'match-anchor'
  /**
   * Mounts the dropdown via a portal to ignore clipping boundaries.
   */
  portaled?: boolean
  id?: string
  ref?: React.Ref<HTMLDivElement>
  keepOpenOnModalClick?: boolean
}

/**
 * The base floating behaviour of a dropdown.
 */
function FloatingDropdown(props: Props) {
  const {
    placement = 'bottom',
    anchorRef,
    boundaryRef,
    triggerRef,
    open = false,
    onClose,
    children,
    anchorOffset = 8,
    disableFlip,
    crossAxisShift = false,
    width,
    autoHeight = false,
    portaled,
    id,
    ref,
    keepOpenOnModalClick,
    ...rest
  } = props

  const dropdownRef = useRef<HTMLDivElement | null>(null)
  const dropdownId = useId()
  const ancestorDropdownIds = useContext(FloatingDropdownContext)
  // Build the complete ancestor chain: this dropdown's ancestors (to be stored on the element)
  const dropdownAncestorIds = ancestorDropdownIds.length > 0 ? ancestorDropdownIds.join(',') : undefined

  const float = useFloating({
    placement,
    strategy: 'fixed',
    middleware: [
      offset({ mainAxis: anchorOffset }),
      ...(!disableFlip ? [flip()] : []),
      shift({
        boundary: boundaryRef?.current ?? undefined,
        crossAxis: crossAxisShift,
        padding: 8,
      }),
      size({
        apply: (args: SizeArgs) => {
          Object.assign(args.elements.floating.style, {
            ...(width === 'match-anchor' ? dynamicWidthStyle(args) : availableWidthStyle(args)),
          })
          if (autoHeight) {
            args.elements.floating.style.setProperty(DROPDOWN_SHEET_MAX_HEIGHT_CSS_VARIABLE, `${Math.max(args.availableHeight, 0)}px`)
          }
        },
        padding: 20,
      }),
    ],
  })

  useEffect(() => {
    if (open && anchorRef.current && dropdownRef.current) {
      return autoUpdate(anchorRef.current, dropdownRef.current, float.update)
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  useEffect(() => {
    if (anchorRef.current) {
      float.refs.setReference(anchorRef.current)
    }
  }, [float.refs.setReference, anchorRef, float.refs])

  const keyDownHandler = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose?.(event, 'escape-key')
    }
  }, [onClose])

  useEffect(() => {
    if (open) {
      window.addEventListener('keydown', keyDownHandler)
    } else {
      window.removeEventListener('keydown', keyDownHandler)
    }

    return () => window.removeEventListener('keydown', keyDownHandler)
  }, [open, keyDownHandler])

  const handleOutsideClick = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    // don't treat clicks inside a modal as outside (e.g. closing modal shouldn't close dropdown)
    if (keepOpenOnModalClick && isClickInModal(event.target as HTMLElement)) {
      return
    }

    // respect elements that explicitly opt out of closing the dropdown
    if (shouldKeepOpenForTarget(event.target as HTMLElement)) {
      return
    }

    // create a truthy array of trigger elements
    const triggerElements = (Array.isArray(triggerRef) ? triggerRef.map(r => r.current) : [triggerRef?.current]).filter(Boolean)

    // Check if the click target is inside a NESTED floating dropdown (child of this dropdown)
    // If so, don't treat it as an outside click to prevent closing parent dropdowns
    // when interacting with nested/child dropdowns.
    // Note: We only prevent closing for nested dropdowns, not sibling dropdowns.
    // Sibling dropdowns (e.g., Guest selector and Date picker) should close each other normally.
    const targetElement = event.target as HTMLElement
    const targetDropdown = targetElement.closest('[data-floating-dropdown]')

    if (targetDropdown && targetDropdown !== dropdownRef.current) {
      // Check if this dropdown contains the target dropdown (nested case - non-portaled)
      const isNestedChild = dropdownRef.current?.contains(targetDropdown)

      if (isNestedChild) {
        // Click is inside a nested child dropdown, don't close parent
        return
      }

      // For portaled dropdowns: check if the target dropdown has this dropdown as an ancestor
      // This handles the case where a child dropdown is portaled to document.body
      // but its ancestor relationship is tracked via data attributes
      // Supports multiple levels of nesting (grandparent -> parent -> child)
      const targetAncestorIds = targetDropdown.getAttribute('data-floating-dropdown-ancestors')
      if (targetAncestorIds?.split(',').includes(dropdownId)) {
        // The target dropdown is a descendant of this dropdown
        return
      }
    }

    if (
      triggerElements.every(element =>
        !element?.contains(event.target as Node) &&
        !element?.contains(document.activeElement),
      )
    ) {
      onClose?.(event, 'outside-click')
    }
  }, [onClose, triggerRef, dropdownId, keepOpenOnModalClick])

  // @ts-expect-error need to use React types in the hook
  useClickOutside(dropdownRef, handleOutsideClick, open)

  useImperativeHandle(ref, () => dropdownRef.current!)

  const isInModal = useIsInModal()

  // Build the new ancestor chain for children: current dropdown ID + all its ancestors
  // Memoized to prevent unnecessary re-renders of nested dropdowns
  const childAncestorIds = useMemo(
    () => [dropdownId, ...ancestorDropdownIds] as const,
    [dropdownId, ancestorDropdownIds],
  )

  const element = <DropdownWrapper
    {...rest}
    id={id}
    data-floating-dropdown
    data-floating-dropdown-id={dropdownId}
    data-floating-dropdown-ancestors={dropdownAncestorIds}
    ref={(element) => {
      dropdownRef.current = element
      float.refs.setFloating(element)
    }}
    style={float.floatingStyles}
    className={clsx(
      getThemeClassName({ mobile: 'default' }),
      { visible: open, isPortaledFromModal: isInModal && portaled, [portaledDropdownClassName]: portaled },
    )}
  >
    <FloatingDropdownContext.Provider value={childAncestorIds}>
      {children}
    </FloatingDropdownContext.Provider>
  </DropdownWrapper>

  const hasMounted = useHasMounted()

  if (!hasMounted) return null

  if (portaled) {
    if (typeof document !== 'undefined') {
      return createPortal(element, document.body)
    } else {
      return null
    }
  }

  return element
}

FloatingDropdown.displayName = 'BaseFloatingDropdown'

export default memo(FloatingDropdown, (prevProps, nextProps) => {
  if (!prevProps.open && !nextProps.open) {
    return true
  }
  return shallowEqual(prevProps, nextProps)
})
